// Manifest registry — keyed by slide id. In production these would be
// fetched from the marketplace API as JSON; here we inline them as
// typed TS so the demo can boot without --resolveJsonModule. The
// canonical authoring format is JSON — see ./examples/*.json for the
// human-readable spec partners would submit.
//
// The dispatcher (SlidePreview.vue) prefers a manifest entry over the
// per-slide Vue component, demonstrating the partner authoring path.

import type { SlidePreviewManifest } from '../../lib/manifestTypes'

export const MANIFESTS: Record<string, SlidePreviewManifest> = {
  'pick-answer': {
    version: 1,
    id: 'pick-answer',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        {
          type: 'stack',
          gap: 6,
          children: [
            { type: 'bar', size: 'xl', emphasis: true },
            { type: 'bar', size: 'md' },
          ],
        },
        { type: 'divider', style: 'dashed' },
        {
          type: 'result-rows',
          options: [
            { label: 'A', pct: 15, count: 4 },
            {
              label: 'B', pct: 54, count: 14, correct: true,
            },
            { label: 'C', pct: 8, count: 2 },
            { label: 'D', pct: 23, count: 6 },
          ],
        },
      ],
    },
    audience: {
      type: 'stack',
      gap: 7,
      children: [
        { type: 'bar', size: 'md', emphasis: true },
        { type: 'option-buttons', labels: ['A', 'B', 'C', 'D'], tapIndex: 1 },
      ],
    },
    loop: { duration: '4s', tapAt: '5%', respondAt: '20%' },
  },

  poll: {
    version: 1,
    id: 'poll',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        { type: 'bar', size: 'xl', emphasis: true },
        { type: 'divider', style: 'dashed' },
        {
          type: 'bar-chart',
          orientation: 'h',
          bars: [
            { label: 'Option A', pct: 62 },
            { label: 'Option B', pct: 24 },
            { label: 'Option C', pct: 14 },
          ],
        },
      ],
    },
    audience: {
      type: 'stack',
      gap: 7,
      children: [
        { type: 'bar', size: 'lg', emphasis: true },
        { type: 'option-buttons', labels: ['A', 'B', 'C'], tapIndex: 0 },
      ],
    },
    loop: { duration: '4s', tapAt: '5%', respondAt: '20%' },
  },

  'word-cloud': {
    version: 1,
    id: 'word-cloud',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        { type: 'bar', size: 'xl', emphasis: true },
        {
          type: 'word-cloud',
          words: [
            { text: 'creative', size: 5 },
            { text: 'fast', size: 3 },
            { text: 'fun', size: 4 },
            { text: 'engaging', size: 6 },
            { text: 'playful', size: 2 },
            { text: 'live', size: 4 },
          ],
          incomingWord: 'amazing',
        },
      ],
    },
    audience: {
      type: 'stack',
      gap: 7,
      children: [
        { type: 'bar', size: 'md', emphasis: true },
        { type: 'text-input', typing: 'amazing' },
        { type: 'send-button', label: 'Send' },
      ],
    },
    loop: { duration: '4s', respondAt: '60%' },
  },

  'pin-on-image': {
    version: 1,
    id: 'pin-on-image',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        { type: 'bar', size: 'xl', emphasis: true },
        {
          type: 'pin-image',
          image: 'placeholder',
          pins: [
            { x: 30, y: 40, intensity: 0.9 },
            { x: 70, y: 60, intensity: 0.6 },
            { x: 50, y: 30, intensity: 0.7 },
            { x: 25, y: 70, intensity: 0.4 },
            { x: 80, y: 25, intensity: 0.5 },
          ],
        },
      ],
    },
    audience: {
      type: 'stack',
      gap: 7,
      children: [
        { type: 'bar', size: 'md', emphasis: true },
        { type: 'image-frame', aspect: 'wide' },
      ],
    },
    loop: { duration: '4s' },
  },

  'spinner-wheel': {
    version: 1,
    id: 'spinner-wheel',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        { type: 'bar', size: 'lg', emphasis: true },
        { type: 'wheel', stopAt: 2, segments: [{}, {}, {}, {}, {}, {}] },
      ],
    },
    audience: {
      type: 'stack',
      gap: 8,
      children: [
        { type: 'bar', size: 'md', emphasis: true },
        { type: 'send-button', label: 'Spin!' },
      ],
    },
    loop: { duration: '5s', tapAt: '5%', respondAt: '10%' },
  },

  'idea-board': {
    version: 1,
    id: 'idea-board',
    presenter: {
      type: 'stack',
      gap: 10,
      children: [
        { type: 'bar', size: 'lg', emphasis: true },
        {
          type: 'sticky-notes',
          notes: [
            {
              x: 8, y: 12, w: 22, h: 26, color: '#FFE8B5',
            },
            {
              x: 36, y: 8, w: 22, h: 24, color: '#D5F0FF',
            },
            {
              x: 64, y: 16, w: 22, h: 26, color: '#FFD3DD',
            },
            {
              x: 12, y: 50, w: 22, h: 24, color: '#D9F3D6',
            },
            {
              x: 44, y: 54, w: 22, h: 26, color: '#E5D5FF',
            },
            {
              x: 72, y: 58, w: 22, h: 24, color: '#FFE8B5',
            },
          ],
        },
      ],
    },
    audience: {
      type: 'stack',
      gap: 7,
      children: [
        { type: 'bar', size: 'md', emphasis: true },
        { type: 'text-input', placeholder: 'Your idea…', typing: 'ship faster' },
        { type: 'send-button', label: 'Add note' },
      ],
    },
    loop: { duration: '5s' },
  },

  'content-slide': {
    version: 1,
    id: 'content-slide',
    presenter: {
      type: 'stack',
      gap: 8,
      children: [
        { type: 'bar', size: 'xl', emphasis: true },
        { type: 'bar', size: 'lg' },
        {
          type: 'stack',
          gap: 6,
          children: [
            { type: 'bar', size: 'md' },
            { type: 'bar', size: 'md' },
            { type: 'bar', size: 'sm' },
          ],
        },
      ],
    },
  },
}
